import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle } from 'lucide-react';

import TextInput from '../../../components/ui/TextInput.jsx';
import Button from '../../../components/ui/Button';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 480px;
  max-width: 92vw;
  max-height: 88vh;
  overflow-y: auto;
  padding: 24px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #A0A8B4;
  &:hover:not(:disabled) { background: #F1F5F9; color: #004242; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const MissingBox = styled.div`
  background: #FFF7ED;
  border: 1px solid #FED7AA;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #9A3412;
`;

const MissingTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  margin-bottom: 6px;
`;

const MissingList = styled.ul`
  margin: 0;
  padding-left: 18px;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #E53935;
  margin-bottom: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

// Faz B (spec 2026-08-07 §4, Task 5) — kapanış diyaloğu:
// - unproposed.actualCount > 0 iken buton DEVRE DIŞI + eksik liste gösterilir
//   (grid DTO'sundan — missingProposals hatası BEKLENMEZ, ama gene de o
//   yoldan dönerse [ör. yarış durumu] aynı liste inline gösterilir).
// - !distributionOk iken gerekçe alanı baştan görünür; sunucu yine de
//   justificationRequired döndürürse (ör. pollingden bağımsız bir taşıma
//   arada dağılımı bozduysa) alan açılır ve kullanıcı gerekçeyle YENİDEN
//   dener — diyalog KAPANMAZ.
export default function CloseSessionDialog({ grid, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [justification, setJustification] = useState('');
  const [showJustification, setShowJustification] = useState(!grid.distributionOk);
  const [missingNames, setMissingNames] = useState(
    (grid.unproposed?.entries || []).map((e) => e.employeeName),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const hasMissing = missingNames.length > 0;

  const handleSubmit = async () => {
    if (hasMissing) return;
    if (showJustification && !justification.trim()) {
      setError(t('performance.calibrationSession.closeDialog.justificationRequiredHint'));
      return;
    }
    setError('');
    setSaving(true);
    const result = await onSubmit(justification.trim());
    setSaving(false);

    if (result.ok) {
      onClose();
      return;
    }

    if (result.message === 'performance.calibrationSession.justificationRequired') {
      setShowJustification(true);
      setError(t('performance.calibrationSession.closeDialog.justificationRequiredHint'));
      return;
    }
    if (result.message === 'performance.calibrationSession.missingProposals') {
      setMissingNames(result.data?.missing || []);
      return;
    }
    setError(t(`responseErrors.${result.message}`, { defaultValue: result.message }));
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{t('performance.calibrationSession.closeDialog.title')}</Title>
          <CloseBtn onClick={onClose} disabled={saving}><X size={18} /></CloseBtn>
        </Header>

        {hasMissing && (
          <MissingBox data-role="missing-proposals-box">
            <MissingTitle><AlertTriangle size={14} /> {t('performance.calibrationSession.closeDialog.missingHint')}</MissingTitle>
            <MissingList>
              {missingNames.map((name, i) => <li key={`${name}-${i}`}>{name}</li>)}
            </MissingList>
          </MissingBox>
        )}

        {showJustification && (
          <Field>
            <TextInput
              label={t('performance.calibrationSession.closeDialog.justificationLabel')}
              type="textarea"
              rows={3}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              required
            />
          </Field>
        )}

        {error && <ErrorText data-role="close-error">{error}</ErrorText>}

        <Footer>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            data-role="confirm-close-btn"
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || hasMissing}
          >
            {saving ? t('common.saving') : t('performance.calibrationSession.closeDialog.confirmButton')}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}
